import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { fetchCoreData, fetchDataLastReceived } from '../services/reportService'
import type { Question, Offer } from '@dashboard/types/config'
import { changeDateToStartAtMidnight } from '@dashboard/services/date'
import { isForm } from '@dashboard/modules/offer/services/offerUtilities'
import { useConfigStore } from '@dashboard/stores/configStore'
import { storeToRefs } from 'pinia'
import { unref, watch } from 'vue'
import { observeResource } from '@dashboard/services/realtimeResourceObserver'
import { createRealtimeQueryReconciler } from '@dashboard/services/realtimeQueryReconciler'
import { DETAIL_PAGES } from '../services/otherDashboards'
import { useRoute } from 'vue-router'
import { dateRangeFromQuery, type ReportingDateRange } from '@dashboard/components/common/dateRange'

export const useReportStore = defineStore('report', () => {
  const configStore = useConfigStore()
  const { currentTeamPid } = storeToRefs(configStore)
  const route = useRoute()
  const onDetailPage = () => Object.values(DETAIL_PAGES).some((page) => page.route === route.name)

  const initialDateRange = (): ReportingDateRange => {
    const inherited = onDetailPage() ? dateRangeFromQuery(route.query) : null
    return inherited ?? { start: changeDateToStartAtMidnight(), end: new Date() }
  }
  const dateRange = ref<ReportingDateRange>(initialDateRange())
  // A picker or a dashboard drill sets this. An implicit range can still move to a restarted test's current run.
  const dateRangeChosen = ref(false)

  const chooseDateRange = (range: ReportingDateRange) => {
    dateRange.value = range
    dateRangeChosen.value = true
  }

  const replaceDateRange = (range: ReportingDateRange) => {
    dateRange.value = range
  }

  const currentReportTeamPid = () => {
    if (currentTeamPid.value === null) {
      throw new Error('Report queries require a current team.')
    }

    return currentTeamPid.value
  }

  watch(currentTeamPid, () => {
    dateRange.value = initialDateRange()
    dateRangeChosen.value = false
  })

  const canFetchReports = computed(() => !!(currentTeamPid.value && dateRange.value))
  const canFetchDetailReports = computed(() => canFetchReports.value && onDetailPage())

  const coreQuery = useQuery({
    queryKey: computed(() => ['coreData', currentTeamPid.value, dateRange.value]),
    queryFn: () => fetchCoreData(currentReportTeamPid(), unref(dateRange)),
    enabled: canFetchDetailReports,
    staleTime: 2 * 60 * 1000
  })

  const {
    data: dataLastReceived,
    isLoading: isLoadingDataLastReceived,
    isFetching: isFetchingDataLastReceived,
    isError: isErrorDataLastReceived,
    dataUpdatedAt: dataLastReceivedUpdatedAt,
    refetch: refetchDataLastReceived
  } = useQuery({
    queryKey: computed(() => ['dataLastReceived', currentTeamPid.value]),
    queryFn: () => fetchDataLastReceived(currentReportTeamPid()),
    enabled: canFetchReports
  })

  const reconcileDataLastReceived = createRealtimeQueryReconciler(
    () => isFetchingDataLastReceived.value,
    () => refetchDataLastReceived()
  )

  watch(
    currentTeamPid,
    (teamPid, _, onCleanup) => {
      if (!teamPid) {
        return
      }

      const controller = new AbortController()
      observeResource(
        String(teamPid),
        'analytics.last-received',
        () => {
          void reconcileDataLastReceived()
        },
        controller.signal
      )
      onCleanup(() => controller.abort())
    },
    { immediate: true }
  )

  const coreData = computed(() => coreQuery.data.value)

  const metricsForQuestion = computed(() => (question: Question) => {
    const questionData = coreData.value?.questionsData?.questionBreakdowns?.find((q) => q.questionId === question.id)
    if (questionData) {
      const visitorsExposed = questionData.visitorsExposed ?? 0
      const visitorsConverted = questionData.visitorsConverted ?? 0
      const conversionRate = visitorsExposed ? 100 * (visitorsConverted / visitorsExposed) : 0
      return [
        {
          label: 'Asks',
          value: visitorsExposed
        },
        {
          label: 'Completions',
          value: visitorsConverted
        },
        { label: 'Answer Rate', value: conversionRate.toFixed(2) + '%' }
      ]
    }
    return [
      {
        label: 'Asks',
        value: 0
      },
      {
        label: 'Completions',
        value: 0
      },
      {
        label: 'Answer Rate',
        value: '0%'
      }
    ]
  })

  const metricsForOffer = computed(() => (offer: Offer) => {
    const offerData = coreData.value?.offersData?.offerBreakdowns?.find((o) => o.offerId === offer.id)
    if (offerData) {
      const visitorsExposed = offerData.visitorsExposed ?? 0
      const visitorsConverted = offerData.visitorsConverted ?? 0
      const conversionRate = visitorsExposed ? 100 * (visitorsConverted / visitorsExposed) : 0
      return [
        {
          label: 'Displays',
          value: visitorsExposed
        },
        {
          label: isForm(offer) ? 'Conversions' : 'Clicks',
          value: visitorsConverted
        },
        {
          label: isForm(offer) ? 'Conversion Rate' : 'Conversion Rate',
          value: conversionRate.toFixed(2) + '%'
        }
      ]
    }
    return [
      {
        label: 'Displays',
        value: 0
      },
      {
        label: isForm(offer) ? 'Conversions' : 'Clicks',
        value: 0
      },
      {
        label: isForm(offer) ? 'Conversion Rate' : 'Conversion Rate',
        value: '0%'
      }
    ]
  })

  return {
    coreQuery,
    dateRange,
    dateRangeChosen,
    chooseDateRange,
    replaceDateRange,
    coreData,
    metricsForQuestion,
    metricsForOffer,
    dataLastReceived,
    isLoadingDataLastReceived,
    isErrorDataLastReceived,
    dataLastReceivedUpdatedAt
  }
})
